<template>
    <!--
      NB: Ingen ytre <div class="bits-facets"> her — den ytre containeren leveres av
      Razor-viewet (Views/Components/Facets.cshtml) som mount-target. Hvis vi pakker
      Vue-templatet i et eget .bits-facets-element får vi to nestede grid-containere
      og layouten kollapser. Multi-root template (fragment) lar mobile-trigger,
      Teleports og .bits-facets__results bli direkte grid-barn av den ytre containeren.
    -->
    <!-- Mobil-trigger er flyttet inn i .bits-facets__results-header (lenger ned)
         så den havner på samme sticky-linje som sortering på mobil. -->

        <!-- Backdrop: kun rendret på mobil. Klikk lukker sheet. -->
        <Teleport to="body" :disabled="!isMobile">
            <div
                v-if="isMobile && !isEmptyCatalogPage"
                class="bits-facets__sheet-backdrop"
                :class="{ 'is-open': mobileSheetOpen }"
                @click="closeMobileSheet"
            ></div>
        </Teleport>

        <!-- Sidebar: teleporteres til body på mobil for å unngå stacking-context
             clamping fra .master-wrapper-content (z-index: 0). På desktop er
             Teleport disabled og sidebar lever inline i .bits-facets-griddet. -->
        <Teleport to="body" :disabled="!isMobile">
            <aside
                ref="sheetEl"
                id="bits-facets-sheet"
                class="bits-facets__sidebar"
                :class="{ 'is-mobile-sheet': isMobile, 'is-open': mobileSheetOpen }"
                v-if="response && !isEmptyCatalogPage"
                :role="isMobile ? 'dialog' : undefined"
                :aria-modal="isMobile ? 'true' : undefined"
                :aria-labelledby="isMobile ? 'bits-facets-sheet-title' : undefined"
                :aria-hidden="isMobile && !mobileSheetOpen ? 'true' : undefined"
            >
            <!-- Sheet-header: kun synlig på mobil (CSS), gir grab-handle, tittel + lukk-knapp. -->
            <div class="bits-facets__sheet-header" v-if="isMobile">
                <span class="bits-facets__sheet-handle" aria-hidden="true"></span>
                <span id="bits-facets-sheet-title" class="bits-facets__sheet-title">Filtrer</span>
                <button type="button" class="bits-facets__sheet-close" @click="closeMobileSheet" aria-label="Lukk filter">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <!-- Valgte filter (chips) + «Nullstill alt» — øverst i filter-panelet
                 (sidebar på desktop, bottom-sheet på mobil), som SH. -->
            <div v-if="activeFilters.length > 0" class="bits-facets__active-filters" aria-label="Valgte filter">
                <button type="button" class="bits-facets__active-reset" @click="clearAll">Nullstill alt</button>
                <button
                    v-for="f in activeFilters"
                    :key="f.key"
                    type="button"
                    class="bits-facets__active-chip"
                    :aria-label="`Fjern filter ${f.label}`"
                    @click="f.remove()"
                >
                    <span class="bits-facets__active-chip-label">{{ f.label }}</span>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <!-- Grupper og pris-blokk rendres som ÉN ordnet liste (sidebarBlocks) slik
                 at editor-styrt fasettrekkefølge fra admin også kan plassere prisen. -->
            <template v-for="block in sidebarBlocks" :key="block.kind === 'group' ? block.group.code : PRICE_GROUP_KEY">
            <details
                v-if="block.kind === 'group'"
                class="bits-facets__group"
                :open="isGroupOpen(block.group.code)"
                @toggle="onGroupToggle(block.group.code, $event)"
            >
                <summary class="bits-facets__group-summary">
                    <span class="bits-facets__group-title">{{ block.group.title }}</span>
                    <span class="bits-facets__chevron" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                </summary>
                <!-- Verdisøk per fasett ved mer enn 5 verdier (som SH.Portal).
                     type="search" gir native tøm-kryss i de fleste nettlesere. -->
                <div v-if="block.group.values.length > 5" class="bits-facets__value-search">
                    <input
                        type="search"
                        :placeholder="`Søk i ${block.group.title.toLowerCase()}…`"
                        :value="facetSearch[block.group.code] ?? ''"
                        :aria-label="`Søk i ${block.group.title}`"
                        @input="onFacetSearch(block.group.code, ($event.target as HTMLInputElement).value)"
                    />
                </div>
                <ul>
                    <li v-if="visibleValues(block.group).length === 0" class="bits-facets__no-match">Ingen treff</li>
                    <li v-for="v in visibleValues(block.group)" :key="v.id">
                        <!-- Hvis gruppa har KUN ÉN verdi, gir det å huke av/på
                             samme resultat — vis den som forhåndsavhuket og inaktiv
                             (tooltip forklarer at filteret allerede er implisitt anvendt). -->
                        <label
                            :class="{ 'is-selected': v.selected, 'is-only': isSingleValueGroup(block.group), 'has-swatch': !!v.colorRgb }"
                            :title="isSingleValueGroup(block.group) ? 'Alle produkter i listen matcher denne verdien' : ''"
                        >
                            <input
                                type="checkbox"
                                :checked="v.selected || isSingleValueGroup(block.group)"
                                :disabled="isSingleValueGroup(block.group)"
                                @change="toggle(block.group.code, v.id, ($event.target as HTMLInputElement).checked)"
                            />
                            <span
                                v-if="v.colorRgb"
                                class="bits-facets__swatch"
                                :style="{ backgroundColor: v.colorRgb }"
                                :title="v.label"
                                aria-hidden="true"
                            ></span>
                            <span class="bits-facets__label">{{ v.label }}</span>
                            <span class="bits-facets__count">{{ v.count }}</span>
                        </label>
                    </li>
                </ul>
            </details>

            <details
                v-else
                class="bits-facets__group bits-facets__group--price"
                :open="isGroupOpen(PRICE_GROUP_KEY)"
                @toggle="onGroupToggle(PRICE_GROUP_KEY, $event)"
            >
                <summary class="bits-facets__group-summary">
                    <span class="bits-facets__group-title">Prisområde</span>
                    <span class="bits-facets__chevron" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </span>
                </summary>
                <div class="bits-facets__price-inputs">
                    <input type="number" :min="response.minPrice" :max="response.maxPrice" v-model.number="priceFromInput" placeholder="Fra" @change="applyPrice" />
                    <input type="number" :min="response.minPrice" :max="response.maxPrice" v-model.number="priceToInput" placeholder="Til" @change="applyPrice" />
                </div>
                <small>{{ formatPrice(response.minPrice) }} – {{ formatPrice(response.maxPrice) }}</small>
            </details>
            </template>

            <button class="bits-facets__clear" type="button" @click="clearAll" v-if="hasSelections">Tøm alle filter</button>

            <!-- Mobil "Vis N produkter"-knapp i bunnen av sheet — lukker sheet og bringer fokus til resultater. -->
            <button
                v-if="isMobile && response"
                class="bits-facets__sheet-cta btn cta"
                type="button"
                @click="closeMobileSheet"
            >
                Vis {{ response.total }} produkter
            </button>
        </aside>
        </Teleport>

        <section class="bits-facets__results">
            <!-- «Mente du …?» (portert fra SH.Portal): vises når søket ser feilstavet ut
                 og en alternativ skrivemåte beviselig gir vesentlig flere treff. Full
                 navigasjon (ikke in-place-søk) så logging, redirects og URL-state følger
                 samme sti som et vanlig søk. -->
            <p v-if="isSearchPage && response && response.suggestedQuery" class="bits-facets__didyoumean">
                Mente du <a :href="suggestedQueryHref">«{{ response.suggestedQuery }}»</a>?
            </p>
            <!-- Innholdssider (artikler/topics). Markupen holdes lik SSR-utgaven i
                 Views/Components/Facets.cshtml — temaer styler ett sett klasser. -->
            <div v-if="contentPages.length > 0" class="bits-facets__external">
                <h2>Innholdssider</h2>
                <ul>
                    <li v-for="(r, i) in contentPages" :key="r.url + i">
                        <a class="bits-facets__external-card" :href="r.url">
                            <!-- Rammen tegnes uansett, også uten bilde: ellers hopper
                                 tekstkolonnen i kort som mangler bilde ut av rutenettet. -->
                            <span class="bits-facets__external-thumb">
                                <img v-if="r.imageUrl" :src="r.imageUrl" alt="" loading="lazy" />
                            </span>
                            <span class="bits-facets__external-body">
                                <span class="bits-facets__external-title">{{ r.title }}</span>
                                <small v-if="r.source" class="bits-facets__external-source">{{ r.source }}</small>
                                <span v-if="r.description" class="bits-facets__external-description">{{ r.description }}</span>
                            </span>
                        </a>
                    </li>
                </ul>
            </div>
            <div class="bits-facets__results-header" role="banner" v-if="response && !isEmptyCatalogPage">
                <!-- Mobil-trigger: åpner sidebar som bottom-sheet. CSS skjuler på desktop. -->
                <button
                    ref="triggerEl"
                    type="button"
                    class="bits-facets__mobile-trigger btn dark w-icon"
                    aria-haspopup="dialog"
                    :aria-expanded="mobileSheetOpen ? 'true' : 'false'"
                    aria-controls="bits-facets-sheet"
                    @click="openMobileSheet"
                >
                    <span class="icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="4" y1="6" x2="20" y2="6" />
                            <line x1="7" y1="12" x2="17" y2="12" />
                            <line x1="10" y1="18" x2="14" y2="18" />
                        </svg>
                    </span>
                    Filter
                    <span v-if="selectionCount > 0" class="bits-facets__mobile-trigger-count">{{ selectionCount }}</span>
                </button>

                <p class="bits-facets__count-summary">
                    {{ countSummary }}
                </p>
                <select v-model="sort" @change="onSortChange" class="bits-facets__sort">
                    <option value="Relevance">Relevans</option>
                    <option value="PriceAsc">Pris stigende</option>
                    <option value="PriceDesc">Pris synkende</option>
                    <option v-if="props.discountSort" value="DiscountDesc">Størst rabatt</option>
                    <option value="Newest">Nyeste</option>
                    <option value="NameAsc">Navn A–Å</option>
                    <option value="NameDesc">Navn Å–A</option>
                </select>
            </div>

            <div v-if="loading" class="bits-facets__loading" aria-live="polite">Laster…</div>
            <div v-else-if="response && response.productIds && response.productIds.length > 0"
                 ref="productsEl"
                 class="bits-facets__products product-grid">
                <div class="item-grid" v-html="cardsHtml"></div>
            </div>
            <p v-else-if="response && !isEmptyCatalogPage" class="bits-facets__empty">Ingen produkter matcher de valgte filtrene.</p>

            <!-- Load-more-modus (default): knapp + fremdriftslinje. -->
            <nav v-if="!isPagerMode && response && response.total > 0" class="bits-facets__loadmore" aria-label="Last flere produkter">
                <p class="bits-facets__loadmore-count">
                    Du har sett <strong>{{ shownCount }}</strong> av <strong>{{ response.total }}</strong>
                </p>
                <div class="bits-facets__loadmore-bar"
                     role="progressbar"
                     :aria-valuemin="0"
                     :aria-valuemax="response.total"
                     :aria-valuenow="shownCount">
                    <div class="bits-facets__loadmore-bar-fill" :style="{ width: progressPct + '%' }"></div>
                </div>
                <button v-if="hasMore"
                        type="button"
                        class="btn cta"
                        :disabled="loading || appending"
                        @click="loadMore">
                    {{ appending ? 'Laster…' : `Last ${nextBatchSize} flere produkter` }}
                </button>
            </nav>

            <!-- Pager-modus: klassisk sidetall-navigasjon. -->
            <nav v-else-if="isPagerMode && response && totalPages > 1" class="bits-facets__pager" aria-label="Sidenavigering">
                <button
                    type="button"
                    class="bits-facets__pager-btn bits-facets__pager-prev"
                    :disabled="page <= 1 || loading"
                    @click="goToPage(page - 1)"
                    aria-label="Forrige side"
                >‹</button>
                <template v-for="(p, i) in pagerPages" :key="i">
                    <span v-if="p === '…'" class="bits-facets__pager-gap" aria-hidden="true">…</span>
                    <button
                        v-else
                        type="button"
                        class="bits-facets__pager-btn"
                        :class="{ 'is-current': p === page }"
                        :disabled="p === page || loading"
                        :aria-current="p === page ? 'page' : undefined"
                        @click="goToPage(p as number)"
                    >{{ p }}</button>
                </template>
                <button
                    type="button"
                    class="bits-facets__pager-btn bits-facets__pager-next"
                    :disabled="page >= totalPages || loading"
                    @click="goToPage(page + 1)"
                    aria-label="Neste side"
                >›</button>
            </nav>
        </section>

        <!-- Flytende "filter-resultat"-toast (portert fra SH.Portal): etter et
             filtervalg tilbyr den resultat-antall + "Til toppen" — men kun mens
             produktgriden er utenfor viewport (typisk når brukeren står langt
             nede i en lang fasettliste). Teleporteres til body av samme
             stacking-context-grunn som mobile-sheet. -->
        <Teleport to="body">
            <Transition name="bits-result-toast">
                <button
                    v-if="resultToastVisible"
                    type="button"
                    class="bits-facets__result-toast"
                    aria-label="Gå til toppen av produktlisten"
                    @click="scrollToTop"
                >
                    <span class="bits-facets__result-toast-count">{{ resultToastLabel }}</span>
                    <span class="bits-facets__result-toast-top">
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <line x1="12" y1="19" x2="12" y2="5" />
                            <polyline points="5 12 12 5 19 12" />
                        </svg>
                        Til toppen
                    </span>
                </button>
            </Transition>
        </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { query, cards as fetchCards, cardsPage as fetchCardsPage, searchClickBeacon, type QueryRequest, type QueryResponse, type FacetGroupDto, type FacetValueDto } from './api';
import { readFromUrl, writeToUrl } from './urlState';

// Innebygd fallback for grupper der redaktøren ikke har valgt standardtilstand
// (group.defaultOpen == null): typisk høy-kardinalitet eller mindre kritiske grupper
// starter lukket. Matchet mot både group.code og group.title (case-insensitive).
const DEFAULT_CLOSED_FACETS = new Set([
    'manufacturer', 'manufacturers', 'merker', 'merke',
    'pris', 'price', 'prisområde'
]);

function isGroupOpenByDefault(group: FacetGroupDto): boolean {
    if (!group) return true;
    // Hvis brukeren allerede har valgt en verdi i gruppa, vis den åpent uansett.
    if (group.values?.some(v => v.selected)) return true;
    // Redaktørens valg fra admin (FacetDisplayCsv) foran den innebygde lista.
    if (group.defaultOpen === true || group.defaultOpen === false) return group.defaultOpen;
    const title = (group.title || '').toLowerCase();
    const code = (group.code || '').toLowerCase();
    return !(DEFAULT_CLOSED_FACETS.has(title) || DEFAULT_CLOSED_FACETS.has(code));
}

// Per-fasett verdisøk (vises ved mer enn 5 verdier, som SH.Portal). Nøkles på
// group.code og beholdes over refresh, så brukeren ikke mister søketeksten når
// resultatene oppdateres etter et filtervalg.
const facetSearch = ref<Record<string, string>>({});

function onFacetSearch(code: string, value: string): void {
    facetSearch.value = { ...facetSearch.value, [code]: value };
}

function visibleValues(group: FacetGroupDto): FacetValueDto[] {
    const q = (facetSearch.value[group.code] ?? '').trim().toLowerCase();
    if (!q) return group.values;
    return group.values.filter(v => v.label.toLowerCase().includes(q));
}

// Hvis en fasett-gruppe har nøyaktig ÉN verdi, betyr det at alle produkter
// i resultatet matcher den verdien — å huke av/på endrer ingenting. Da viser
// vi den som forhåndsavhuket og inaktiv (`disabled`) i template, så brukeren
// får visuell bekreftelse på at filteret er anvendt uten å kunne velge bort.
function isSingleValueGroup(group: FacetGroupDto): boolean {
    // Kampanjegruppen har alltid nøyaktig én verdi («På kampanje») og er et ekte
    // valg — serveren tar den bort når hele resultatet er på kampanje.
    if (group?.code === 'campaign') return false;
    return !!group && Array.isArray(group.values) && group.values.length === 1;
}

const props = defineProps<{
    categoryId?: number | null;
    manufacturerId?: number | null;
    vendorId?: number | null;
    initialText?: string;
    isSearchPage?: boolean;
    pageSize?: number;
    pagingMode?: string;
    discountSort?: boolean;
    initialResponse?: QueryResponse | null;
    initialCardsHtml?: string;
}>();

// "loadmore" (knapp + fremdriftslinje, default) eller "pager" (sidetall).
const isPagerMode = computed(() => props.pagingMode === 'pager');

const selected = ref<Record<string, number[]>>({});
const priceFrom = ref<number | null>(null);
const priceTo = ref<number | null>(null);
const priceFromInput = ref<number | null>(null);
const priceToInput = ref<number | null>(null);
const sort = ref<string>('Relevance');
const page = ref<number>(1);
const text = ref<string>(props.initialText ?? '');
const response = ref<QueryResponse | null>(null);
const cardsHtml = ref<string>('');

// Temaer lazy-laster produktbilder (img[data-src]), men theme-hookene kjører på
// DOMContentLoaded — før Vue har satt inn kortene. Pluginen eier derfor sin egen
// IntersectionObserver med samme klasse-konvensjoner (img-loaded/image-loaded),
// så bildene fungerer uten theme-støtte.
let lazyImageObserver: IntersectionObserver | null = null;

function loadLazyImage(img: HTMLImageElement): void {
    const src = img.getAttribute('data-src');
    if (!src) return;
    img.src = src;
    img.removeAttribute('data-src');
    window.setTimeout(() => {
        img.classList.add('img-loaded');
        img.parentElement?.parentElement?.classList.add('image-loaded');
    }, 200);
}

function ensureLazyImages(): void {
    if (!lazyImageObserver) {
        // threshold: 0 (ikke 0.1): placeholder-bilder (1×1 pixel.gif) kan ha
        // ~null areal, og da når intersectionRatio aldri en terskel > 0.
        lazyImageObserver = new IntersectionObserver((entries, observer) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                loadLazyImage(entry.target as HTMLImageElement);
                observer.unobserve(entry.target);
            }
        }, { threshold: 0, rootMargin: '300px 0px' });
    }
    document.querySelectorAll<HTMLImageElement>('.bits-facets img[data-src]').forEach(img => {
        // Bilder i (eller nær) viewport lastes umiddelbart via geometri-sjekk,
        // uavhengig av observer-quirks; resten tar observeren ved scroll.
        const r = img.getBoundingClientRect();
        if (r.top < window.innerHeight + 300 && r.bottom > -300) {
            loadLazyImage(img);
        } else {
            lazyImageObserver!.observe(img);
        }
    });
}

// Signaturen (produkt-ID-ene i rekkefølge) til settet vi sist meldte fra om.
// Tom streng = ingenting meldt ennå.
let lastViewedSignature = '';

/**
 * Leser produktdata fra data-bs-*-attributtene kortene bærer (issue #318).
 * Markupen kommer fra _ProductCards.cshtml; finner vi ingen attributter, er
 * butikken på en eldre pakkeversjon og vi melder ingenting.
 */
function collectCardItems(): Array<Record<string, unknown>> {
    const root = productsEl.value;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>('[data-bs-product-id]')).map((el, index) => {
        const price = el.dataset.bsProductPrice;
        return {
            id: Number(el.dataset.bsProductId),
            name: el.dataset.bsProductName ?? '',
            sku: el.dataset.bsProductSku ?? '',
            price: price ? Number(price) : null,
            brand: el.dataset.bsProductBrand || null,
            index,
        };
    });
}

/**
 * Meldingen analyseverktøy skal lytte på (issue #318). Til forskjell fra
 * `bits-search:cards-rendered`, som bevisst fyres flere ganger per oppdatering
 * (lazy-bilder, layout-settling), fyres denne ÉN gang per faktisk endring av
 * kortsettet — ellers ville hver visning telt dobbelt i GA.
 *
 * Deduperingen er signaturbasert, ikke antallsbasert: et filterbytte kan gi like
 * mange kort som før, men andre produkter, og det er en ny visning.
 * `appended` skiller «last flere» fra et nytt sett, og `newItems` er da bare
 * de som kom til.
 */
function notifyItemsViewed(): void {
    const items = collectCardItems();
    if (items.length === 0) return;

    const signature = items.map(i => i.id).join(',');
    if (signature === lastViewedSignature) return;

    const appended = lastViewedSignature !== '' && signature.startsWith(lastViewedSignature + ',');
    const newItems = appended ? items.slice(lastViewedSignature.split(',').length) : items;
    lastViewedSignature = signature;

    document.dispatchEvent(new CustomEvent('bits-search:items-viewed', {
        detail: { items, newItems, appended },
    }));
}

// Fyres etter hver DOM-oppdatering av kortene. Scroll-eventet trigger temaer
// med scroll-basert lazy-loading; custom-eventet lar temaer koble på egen
// per-kort-logikk (sliders, sporing osv.).
function notifyCardsRendered(): void {
    ensureLazyImages();
    window.dispatchEvent(new Event('scroll'));
    document.dispatchEvent(new CustomEvent('bits-search:cards-rendered'));
}

// flush: 'post' er påkrevd: med default pre-flush kan callbacken (selv via
// nextTick) kjøre før v-html-patchen har satt inn kortene i DOM.
watch(cardsHtml, () => {
    void nextTick().then(() => {
        notifyCardsRendered();
        notifyItemsViewed();
        // Ekstra kall etter layout-settling — idempotent og billig. notifyItemsViewed
        // er selv-deduperende, så den er med her kun for å fange tilfellet der kortene
        // ikke var i DOM ennå ved første forsøk.
        window.setTimeout(() => {
            notifyCardsRendered();
            notifyItemsViewed();
        }, 150);
    });
}, { flush: 'post' });
// Tracker hvilke fasett-grupper som er åpne. Brukes med @toggle-event slik at
// brukerens klikk på <summary> faktisk overlever Vue's re-render (en reactive
// :open-binding ville ellers overstyrt browser-native toggle).
const openGroups = ref<Set<string>>(new Set());
// Spesiell key for pris-gruppen siden den ikke er i response.facets-loopen.
const PRICE_GROUP_KEY = '__price__';

// Brukerens eksplisitte åpne/lukk-valg, husket på tvers av sidelastinger
// (localStorage). Gruppekodene er globale (category, manufacturer, spec-N, …),
// så valget gjelder på tvers av kategorisider. KUN bevisste valg lagres —
// grupper uten lagret valg følger fortsatt isGroupOpenByDefault, slik at
// endrede defaults slår gjennom for uberørte grupper.
const OPEN_GROUPS_STORAGE_KEY = 'bits-search:open-facets';

function loadOpenOverrides(): Record<string, boolean> {
    try {
        const raw = window.localStorage.getItem(OPEN_GROUPS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) as unknown : null;
        return parsed && typeof parsed === 'object' ? parsed as Record<string, boolean> : {};
    } catch {
        return {}; // privat modus / blokkert storage — da gjelder kun defaults
    }
}

const openOverrides = loadOpenOverrides();

function rememberGroupOpen(key: string, open: boolean): void {
    openOverrides[key] = open;
    try {
        window.localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(openOverrides));
    } catch {
        // Storage utilgjengelig — valget gjelder da bare inneværende sidevisning.
    }
}

// Sjekk om gruppe er åpen — brukes som :open-binding på <details>.
function isGroupOpen(key: string): boolean {
    return openGroups.value.has(key);
}

// Native <details>-element toggler `open`-attributtet før Vue ser eventet.
// Vi synker state etter handlingen. Set re-instansieres for å trigge reaktiv re-render.
function onGroupToggle(key: string, e: Event): void {
    const el = e.target as HTMLDetailsElement;
    // Brukerklikk kjennetegnes ved at DOM-en er FORAN state (browseren togglet
    // attributtet selv). Når Vue selv setter :open i en re-render, matcher
    // attributtet allerede state — da skal det ikke lagres som brukervalg.
    const isUserAction = el.open !== isGroupOpen(key);
    const next = new Set(openGroups.value);
    if (el.open) next.add(key); else next.delete(key);
    openGroups.value = next;
    if (isUserAction) rememberGroupOpen(key, el.open);
}

// Initialiser openGroups når response endrer seg — lagret brukervalg vinner,
// ellers brukes isGroupOpenByDefault.
watch(response, (r) => {
    if (!r) return;
    const next = new Set<string>();
    if (r.facets) {
        for (const g of r.facets) {
            if (openOverrides[g.code] ?? isGroupOpenByDefault(g)) next.add(g.code);
        }
    }
    // Pris er en egen <details> — lagret valg, ellers sesjonsvalget, ellers redaktørens
    // standard (r.priceDefaultOpen); uten noe av dette starter den lukket.
    if (openOverrides[PRICE_GROUP_KEY] ?? (openGroups.value.has(PRICE_GROUP_KEY) || r.priceDefaultOpen === true)) {
        next.add(PRICE_GROUP_KEY);
    }
    openGroups.value = next;
}, { immediate: true });
// `loading` brukes for full re-render (filter-/sort-endring). `appending` brukes
// når load-more legger til neste side — da skal eksisterende kort fortsatt vises.
const loading = ref<boolean>(false);
const appending = ref<boolean>(false);
let pending: AbortController | null = null;
let pendingCards: AbortController | null = null;

// Mobil bottom-sheet — sidebar teleporteres til body på små skjermer og
// vises som modal-sheet over content. Trigger-knapp + backdrop ligger inline.
const isMobile = ref<boolean>(false);
const mobileSheetOpen = ref<boolean>(false);
const triggerEl = ref<HTMLButtonElement | null>(null);
const sheetEl = ref<HTMLElement | null>(null);
let mobileMq: MediaQueryList | null = null;

function syncIsMobile(): void {
    isMobile.value = !!mobileMq?.matches;
    // Hvis vi resizer fra mobil → desktop mens sheet er åpen, lukk + frigi body-scroll
    if (!isMobile.value && mobileSheetOpen.value) {
        closeMobileSheet();
    }
}

function openMobileSheet(): void {
    mobileSheetOpen.value = true;
    document.body.style.overflow = 'hidden';
    // Flytt fokus inn i sheet-en etter at den er montert/vist. nextTick via
    // setTimeout(0) er nok — Vue rerendrer aria-attributter og toggler is-open
    // synkront, men focus() må vente på paint.
    setTimeout(() => {
        const closeBtn = sheetEl.value?.querySelector<HTMLElement>('.bits-facets__sheet-close');
        closeBtn?.focus();
    }, 0);
}

function closeMobileSheet(): void {
    mobileSheetOpen.value = false;
    document.body.style.overflow = '';
    // Returner fokus til trigger-knappen så tastatur-brukere ikke mister stedet
    // sitt. Skjer kun hvis trigger er synlig (på mobil) — på desktop er den
    // skjult og focus() er no-op.
    setTimeout(() => triggerEl.value?.focus(), 0);
}

// ESC-handler: lukker sheet hvis åpen. Lyttes globalt på document siden
// fokus kan være på et hvilket som helst element inni sheet-en.
function onSheetKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && mobileSheetOpen.value) {
        e.preventDefault();
        closeMobileSheet();
    }
}

// ---- Flytende "filter-resultat"-toast (portert fra SH.Portal) ----
// Etter et filtervalg viser vi en flytende "Viser N produkter / Til toppen"-knapp.
// Den auto-skjules IKKE på tid — i stedet vises den kun mens ingen produkter er
// i viewport, så den dukker opp når brukeren har scrollet forbi griden (lang
// fasettliste) og forsvinner straks produktene er synlige igjen.
const filterResultVisible = ref<boolean>(false);
const productsInView = ref<boolean>(true);
const productsEl = ref<HTMLElement | null>(null);
let toastRafId: number | null = null;

// Hvor mange px av produktgriden som kan stå igjen øverst i viewport før vi
// fortsatt regner produktene som "ute av syne". Høyere = toast vises tidligere
// (mens en liten stripe av kortene fortsatt synes). 0 = kun når helt forbi.
const VIEW_TOLERANCE = 300;

function computeProductsInView(): void {
    const grid = productsEl.value;
    if (!grid) {
        productsInView.value = false;
        return;
    }
    const r = grid.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    productsInView.value = r.bottom > VIEW_TOLERANCE && r.top < vh;
}

function onToastScrollOrResize(): void {
    if (toastRafId !== null) return;
    toastRafId = requestAnimationFrame(() => {
        toastRafId = null;
        computeProductsInView();
    });
}

// Skjules også under lasting (griden er unmountet da) og mens mobile-sheet er
// åpen (sheet-CTA-en "Vis N produkter" dekker samme behov der).
const resultToastVisible = computed(() =>
    filterResultVisible.value && !productsInView.value && !loading.value && !mobileSheetOpen.value);

// "Viser 1 produkt" vs "Viser N produkter".
const resultToastLabel = computed(() => {
    const n = response.value?.total ?? 0;
    return `Viser ${n} ${n === 1 ? 'produkt' : 'produkter'}`;
});

function scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

// Re-sjekk viewport når filtrerte resultater er inne (griden ble nettopp byttet ut).
watch([filterResultVisible, cardsHtml], () => {
    void nextTick().then(computeProductsInView);
}, { flush: 'post' });

// Antall aktive valg (vises som badge på trigger-knappen)
const selectionCount = computed(() => {
    let n = Object.values(selected.value).reduce((s, ids) => s + ids.length, 0);
    if (priceFrom.value != null) n++;
    if (priceTo.value != null) n++;
    return n;
});

const totalPages = computed(() => response.value
    ? Math.max(1, Math.ceil(response.value.total / response.value.pageSize))
    : 1);

// Load-more: hvor mange kort er vist totalt (kumulativt på tvers av sider)
const shownCount = computed(() => {
    if (!response.value) return 0;
    return Math.min(response.value.page * response.value.pageSize, response.value.total);
});

const progressPct = computed(() => {
    if (!response.value || response.value.total === 0) return 0;
    return Math.min(100, (shownCount.value / response.value.total) * 100);
});

const hasMore = computed(() => {
    if (!response.value) return false;
    return shownCount.value < response.value.total;
});

const nextBatchSize = computed(() => {
    if (!response.value) return props.pageSize ?? 24;
    const remaining = response.value.total - shownCount.value;
    return Math.min(response.value.pageSize, remaining);
});

// Pager-modus: vindu av sidetall rundt gjeldende side, med 1/siste + ellipser.
const pagerPages = computed<(number | string)[]>(() => {
    const total = totalPages.value;
    const cur = page.value;
    const win = 2; // hvor mange sider på hver side av gjeldende
    const out: (number | string)[] = [];
    const lo = Math.max(1, cur - win);
    const hi = Math.min(total, cur + win);
    if (lo > 1) {
        out.push(1);
        if (lo > 2) out.push('…');
    }
    for (let p = lo; p <= hi; p++) out.push(p);
    if (hi < total) {
        if (hi < total - 1) out.push('…');
        out.push(total);
    }
    return out;
});

const hasSelections = computed(() =>
    Object.values(selected.value).some(v => v.length > 0) ||
    priceFrom.value != null ||
    priceTo.value != null);

// Tom kategori-/merkeside: ingen produkter i det hele tatt OG ingen filtre valgt.
// Da har verken filterlisten, sorteringen eller «ingen treff»-teksten noe å gjøre
// der — hele modulen holder seg unna og lar temaets kategoritekst stå alene.
// Søkesiden er unntatt: der ER «ingen treff» svaret på det kunden spurte om, og
// et filtrert null-treff må beholde filtrene så kunden kan angre valget sitt.
const isEmptyCatalogPage = computed(() =>
    !props.isSearchPage &&
    !props.initialText &&
    !!response.value &&
    response.value.total === 0 &&
    !hasSelections.value);

// Valgte filter som chips (som SH): én per valgt fasettverdi + evt. pris.
// Etikettene slås opp i response.facets (serveren beholder valgte verdier der,
// også når count = 0). Hver chip har en remove()-handler.
interface ActiveFilter { key: string; label: string; remove: () => void; }

const activeFilters = computed<ActiveFilter[]>(() => {
    const out: ActiveFilter[] = [];
    const facets = response.value?.facets ?? [];
    for (const [code, ids] of Object.entries(selected.value)) {
        const group = facets.find(g => g.code === code);
        for (const id of ids) {
            const label = group?.values.find(v => v.id === id)?.label ?? `#${id}`;
            out.push({ key: `${code}:${id}`, label, remove: () => toggle(code, id, false) });
        }
    }
    if (priceFrom.value != null || priceTo.value != null) {
        const from = priceFrom.value != null ? formatPrice(priceFrom.value) : '';
        const to = priceTo.value != null ? formatPrice(priceTo.value) : '';
        const label = from && to ? `${from} – ${to}` : from ? `fra ${from}` : `til ${to}`;
        out.push({ key: 'price', label, remove: clearPrice });
    }
    return out;
});

function clearPrice(): void {
    priceFrom.value = priceFromInput.value = null;
    priceTo.value = priceToInput.value = null;
    page.value = 1;
    void refresh(true).then(() => { filterResultVisible.value = true; });
}

// Sidebar-blokkene (fasettgrupper + pris) som ÉN ordnet liste. Backenden sender
// pricePosition (= antall grupper før pris-blokken, styrt av "price"-koden i
// admin-fasettrekkefølgen); utelatt/stort tall betyr etter alle gruppene.
type SidebarBlock = { kind: 'group'; group: FacetGroupDto } | { kind: 'price' };

const sidebarBlocks = computed<SidebarBlock[]>(() => {
    if (!response.value) return [];
    const blocks: SidebarBlock[] = (response.value.facets ?? []).map(g => ({ kind: 'group', group: g }));
    if (response.value.maxPrice > 0 && !response.value.priceHidden) {
        const pos = Math.min(Math.max(response.value.pricePosition ?? blocks.length, 0), blocks.length);
        blocks.splice(pos, 0, { kind: 'price' });
    }
    return blocks;
});

// Innholdstreff vises kun på søkesiden, og som et kompakt rutenett over produktene.
// API-et leverer opptil 20 — flere enn seks kort skyver produkttreffene under folden,
// så resten kuttes her (samme tak som SSR-utgaven i view-komponenten).
const MaxContentPages = 6;

const contentPages = computed(() => {
    if (!props.isSearchPage) return [];
    return (response.value?.externalResults ?? []).slice(0, MaxContentPages);
});

// «Mente du …?»-lenken går til et nytt søk på søkesiden (samme sti som et vanlig søk).
const suggestedQueryHref = computed(() => {
    const suggestion = response.value?.suggestedQuery;
    return suggestion ? `/search?q=${encodeURIComponent(suggestion)}` : '#';
});

const countSummary = computed(() => {
    if (!response.value) return '';
    const start = (response.value.page - 1) * response.value.pageSize + 1;
    const end = Math.min(response.value.page * response.value.pageSize, response.value.total);
    return response.value.total === 0
        ? 'Ingen produkter'
        : `Viser ${start}–${end} av ${response.value.total} produkter`;
});

function formatPrice(p: number): string {
    return new Intl.NumberFormat('nb-NO').format(p) + ' kr';
}

function buildRequest(pageNum: number, size: number): QueryRequest {
    return {
        categoryId: props.categoryId ?? null,
        manufacturerId: props.manufacturerId ?? null,
        vendorId: props.vendorId ?? null,
        text: text.value || null,
        selected: selected.value,
        priceFrom: priceFrom.value,
        priceTo: priceTo.value,
        sort: sort.value,
        page: pageNum,
        pageSize: size
    };
}

async function refresh(push = false, append = false): Promise<void> {
    pending?.abort();
    const ctrl = new AbortController();
    pending = ctrl;
    if (append) {
        appending.value = true;
    } else {
        loading.value = true;
    }

    const originalPageSize = props.pageSize ?? 24;

    // "Akkumulert initial-load": hvis page > 1 i URL (typisk etter back-navigasjon
    // fra et produkt), hent alle produkter opp til og med den siden i ÉN forespørsel
    // via økt pageSize. Sørger for at brukeren ser samme antall kort som før de
    // navigerte vekk, i stedet for bare den siste siden.
    // Akkumulert initial-last gjelder kun load-more-modus (vis alle kort opp til
    // siden brukeren var på). I pager-modus vises ÉN side om gangen.
    const isAccumulatedInitialLoad = !isPagerMode.value && !append && page.value > 1;
    const effectivePage = isAccumulatedInitialLoad ? 1 : page.value;
    const effectivePageSize = isAccumulatedInitialLoad
        ? page.value * originalPageSize
        : originalPageSize;

    const request = buildRequest(effectivePage, effectivePageSize);

    try {
        const r = await query(request, ctrl.signal);
        // Behold logisk page-state i response så `shownCount`, `hasMore` osv. stemmer
        // med URL-en (page=N, pageSize=24) selv om vi faktisk fetchet page=1 + pageSize=N*24.
        if (isAccumulatedInitialLoad) {
            response.value = { ...r, page: page.value, pageSize: originalPageSize };
        } else {
            response.value = r;
        }
        // Skriv URL med logisk state (ikke det forstørrede pageSize-callet)
        const urlRequest = isAccumulatedInitialLoad
            ? { ...request, page: page.value, pageSize: originalPageSize }
            : request;
        writeToUrl(urlRequest, !push);
        await loadCards(r.productIds, append);
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('BitsSearch query failed', e);
    } finally {
        if (pending === ctrl) {
            loading.value = false;
            appending.value = false;
        }
    }
}

async function loadCards(ids: number[], append = false): Promise<void> {
    pendingCards?.abort();
    const ctrl = new AbortController();
    pendingCards = ctrl;
    try {
        const html = await fetchCards(ids, ctrl.signal);
        cardsHtml.value = append ? cardsHtml.value + html : html;
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('BitsSearch cards failed', e);
        if (!append) cardsHtml.value = '';
    }
}

async function loadMore(): Promise<void> {
    if (!response.value || appending.value) return;
    // Én rundtur: /cardspage paginerer (uten fasett-rekomputering) og rendrer kortene.
    // Fasetter/total endrer seg ikke ved paginering, så vi beholder dem og bumper kun page.
    pendingCards?.abort();
    const ctrl = new AbortController();
    pendingCards = ctrl;
    appending.value = true;

    const nextPage = page.value + 1;
    const request = buildRequest(nextPage, props.pageSize ?? 24);

    try {
        const html = await fetchCardsPage(request, ctrl.signal);
        if (pendingCards !== ctrl) return; // avløst av en nyere forespørsel
        cardsHtml.value = cardsHtml.value + html;
        page.value = nextPage;
        // Behold fasetter/total fra forrige fulle spørring; oppdater kun page så
        // shownCount/hasMore/progress følger med.
        response.value = { ...response.value, page: nextPage };
        writeToUrl(request, true);
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('BitsSearch cardspage failed', e);
    } finally {
        if (pendingCards === ctrl) appending.value = false;
    }
}

// NB: Vi scroller bevisst IKKE til toppen ved filterendring — brukeren skal bli
// stående der hen er (typisk midt i fasettlisten). I stedet vises den flytende
// resultat-toasten når de nye resultatene er inne.
function toggle(code: string, id: number, checked: boolean): void {
    const current = new Set(selected.value[code] ?? []);
    if (checked) current.add(id);
    else current.delete(id);
    if (current.size === 0) delete selected.value[code];
    else selected.value[code] = [...current];
    page.value = 1;
    void refresh(true).then(() => { filterResultVisible.value = true; });
}

function applyPrice(): void {
    priceFrom.value = priceFromInput.value;
    priceTo.value = priceToInput.value;
    page.value = 1;
    void refresh(true).then(() => { filterResultVisible.value = true; });
}

function onSortChange(): void {
    page.value = 1;
    void refresh(true).then(() => { filterResultVisible.value = true; });
}

// Pager-modus: hopp til side p. Som loadMore én /cardspage-rundtur (ingen
// fasett-rekompute), men ERSTATTER kortene i stedet for å legge til. Fasetter/
// total er uendret ved sidebytte, så vi beholder dem og bumper kun page.
async function goToPage(p: number): Promise<void> {
    if (!response.value || p === page.value || p < 1 || p > totalPages.value || loading.value) return;
    pendingCards?.abort();
    const ctrl = new AbortController();
    pendingCards = ctrl;
    loading.value = true;
    const request = buildRequest(p, props.pageSize ?? 24);
    try {
        const html = await fetchCardsPage(request, ctrl.signal);
        if (pendingCards !== ctrl) return; // avløst
        cardsHtml.value = html;
        page.value = p;
        response.value = { ...response.value, page: p };
        writeToUrl(request, false); // push: pager-navigering skal kunne gås tilbake
        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('BitsSearch pager failed', e);
    } finally {
        if (pendingCards === ctrl) loading.value = false;
    }
}

function clearAll(): void {
    selected.value = {};
    priceFrom.value = priceFromInput.value = null;
    priceTo.value = priceToInput.value = null;
    page.value = 1;
    // Ved "Tøm alle filter" scroller vi til toppen (hele lista er "ny"),
    // og da trengs ingen resultat-toast.
    filterResultVisible.value = false;
    scrollToTop();
    refresh(true);
}

// «Størst rabatt» finnes bare når butikken har slått den på — en gammel lenke med
// s=DiscountDesc skal da vise standardsorteringen, ikke en tom nedtrekksliste.
function allowedSort(value: string): string {
    return value === 'DiscountDesc' && !props.discountSort ? 'Relevance' : value;
}

function applyUrlState(): void {
    const s = readFromUrl();
    if (s.selected) selected.value = s.selected;
    if (s.priceFrom != null) priceFromInput.value = priceFrom.value = s.priceFrom;
    if (s.priceTo != null) priceToInput.value = priceTo.value = s.priceTo;
    if (s.sort) sort.value = allowedSort(s.sort);
    if (s.page) page.value = s.page;
    if (s.text != null) text.value = s.text;
}

function hydrateFromServer(): boolean {
    if (!props.initialResponse) return false;
    response.value = props.initialResponse;
    cardsHtml.value = props.initialCardsHtml ?? '';
    if (props.initialResponse.sort) sort.value = props.initialResponse.sort;
    if (props.initialResponse.page) page.value = props.initialResponse.page;
    return true;
}

// Søkeanalyse: klikk på produktkort i søkeresultatene sendes som beacon (best
// effort, øker ClickCount på dagens BS_SearchLog-rad). Kun på søkesiden og kun
// når et tekstsøk er aktivt — kategoribrowsing uten søkeord skal IKKE telle.
// Delegert på document: kortene er v-html-injisert server-HTML uten Vue-handlers,
// og griden byttes ut ved hver oppdatering. Kun klikk på lenker/knapper i kortet
// teller (bomklikk på kort-flaten skal ikke blåse opp CTR).
function onResultClick(e: MouseEvent): void {
    if (!props.isSearchPage) return;
    const activeQuery = text.value.trim();
    if (!activeQuery) return;

    const target = e.target as HTMLElement | null;
    if (!target?.closest('a, button')) return;
    const card = target.closest('.bits-facets__products [data-productid]');
    if (!card) return;

    const productId = Number(card.getAttribute('data-productid'));
    if (productId > 0) searchClickBeacon(activeQuery, productId);
}

onMounted(() => {
    applyUrlState();
    if (!hydrateFromServer()) {
        refresh(false);
    }
    window.addEventListener('popstate', () => {
        applyUrlState();
        refresh(false);
    });

    // Sett opp mobile media-query-listener (1023px og under = mobil/tablet)
    mobileMq = window.matchMedia('(max-width: 1023px)');
    syncIsMobile();
    if (mobileMq.addEventListener) {
        mobileMq.addEventListener('change', syncIsMobile);
    } else if ((mobileMq as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener) {
        (mobileMq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(syncIsMobile);
    }

    // Globalt ESC-keydown for å lukke mobile bottom-sheet. Lyttes på document
    // siden fokus kan være på et hvilket som helst element inni sheet-en.
    document.addEventListener('keydown', onSheetKeydown);

    // Resultat-toast: følg med på om produktgriden er i viewport (rAF-throttlet).
    window.addEventListener('scroll', onToastScrollOrResize, { passive: true });
    window.addEventListener('resize', onToastScrollOrResize, { passive: true });
    computeProductsInView();

    // Klikk-beacon for søkeanalysen (kun aktiv på søkesiden, se onResultClick).
    document.addEventListener('click', onResultClick);
});

onUnmounted(() => {
    if (mobileMq?.removeEventListener) {
        mobileMq.removeEventListener('change', syncIsMobile);
    }
    document.removeEventListener('keydown', onSheetKeydown);
    document.removeEventListener('click', onResultClick);
    window.removeEventListener('scroll', onToastScrollOrResize);
    window.removeEventListener('resize', onToastScrollOrResize);
    if (toastRafId !== null) cancelAnimationFrame(toastRafId);
    // Frigi body-scroll-lock hvis komponenten unmountes mens sheet er åpen
    document.body.style.overflow = '';
});

watch(() => props.initialText, (v) => {
    text.value = v ?? '';
    page.value = 1;
    refresh(false);
});
</script>
